import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { racetrack as racetrackApi } from '../../../utils/api';
import { toast } from 'react-hot-toast';
import { PREDEFINED_RACETRACKS } from '../../../constants/predefinedRacetracks';
import ReactCountryFlag from 'react-country-flag';
import { COUNTRY_TO_CODE } from '../../../utils/countryUtils';

// Import the countries translations
import countriesEn from '../../../constants/countries/en.json';
import countriesEs from '../../../constants/countries/es.json';
import countriesCa from '../../../constants/countries/ca.json';

const countryTranslations = {
  en: countriesEn,
  es: countriesEs,
  ca: countriesCa
};

const ImportRacetrackModal = ({ open, onClose, onSuccess }) => {
  const { t, i18n } = useTranslation(['racetracks', 'common']);
  const [importing, setImporting] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedTrack, setSelectedTrack] = useState('');

  // Get translated country name
  const getCountryName = (countryCode) => {
    const translations = countryTranslations[i18n.language] || countryTranslations.en;
    return translations[countryCode] || countryCode;
  };

  // Group tracks by country
  const tracksByCountry = useMemo(() => {
    return PREDEFINED_RACETRACKS.reduce((acc, track) => {
      if (!acc[track.country]) {
        acc[track.country] = [];
      }
      acc[track.country].push(track);
      return acc;
    }, {});
  }, []);

  // Available countries
  const availableCountries = useMemo(() => {
    return Object.keys(tracksByCountry).sort((a, b) => 
      getCountryName(a).localeCompare(getCountryName(b))
    );
  }, [i18n.language]);

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
    setSelectedTrack('');
  };

  const handleTrackChange = (event) => {
    setSelectedTrack(event.target.value);
  };

  const handleImport = async () => {
    if (!selectedTrack) return;

    const track = PREDEFINED_RACETRACKS.find(t => t.name === selectedTrack);
    if (!track) return;

    try {
      setImporting(true);
      await racetrackApi.create({
        name: track.name,
        location: track.location,
        country: track.country,
        length: track.length
      });

      toast.success(t('importModal.success'));
      onSuccess();
      setSelectedCountry('');
      setSelectedTrack('');
    } catch (error) {
      console.error('Error importing racetrack:', error);
      toast.error(t('importModal.error'));
    } finally {
      setImporting(false);
    }
  };

  const selectedTrackData = PREDEFINED_RACETRACKS.find(t => t.name === selectedTrack);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('importModal.title')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('importModal.description')}
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('importModal.selectCountry')}</InputLabel>
            <Select
              value={selectedCountry}
              onChange={handleCountryChange}
              label={t('importModal.selectCountry')}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {COUNTRY_TO_CODE[selected] && (
                    <ReactCountryFlag 
                      countryCode={COUNTRY_TO_CODE[selected]} 
                      svg 
                      style={{ 
                        width: '1.5em', 
                        height: '1.5em',
                        marginRight: '8px',
                        borderRadius: '2px'
                      }}
                    />
                  )}
                  {getCountryName(selected)}
                </Box>
              )}
            >
              {availableCountries.map(country => (
                <MenuItem key={country} value={country}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {COUNTRY_TO_CODE[country] && (
                      <ReactCountryFlag 
                        countryCode={COUNTRY_TO_CODE[country]} 
                        svg 
                        style={{ 
                          width: '1.5em', 
                          height: '1.5em',
                          marginRight: '8px',
                          borderRadius: '2px'
                        }}
                      />
                    )}
                    {getCountryName(country)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedCountry && (
            <FormControl fullWidth>
              <InputLabel>{t('importModal.selectTrack')}</InputLabel>
              <Select
                value={selectedTrack}
                onChange={handleTrackChange}
                label={t('importModal.selectTrack')}
              >
                {tracksByCountry[selectedCountry]?.map(track => (
                  <MenuItem key={track.name} value={track.name}>
                    {track.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {selectedTrackData && (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {selectedTrackData.name}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2" color="text.secondary">
                  {selectedTrackData.location}
                </Typography>
                <Chip 
                  label={`${selectedTrackData.length.toFixed(3)} km`}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {selectedTrackData.description}
              </Typography>
            </CardContent>
          </Card>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('common:close')}
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!selectedTrack || importing}
          startIcon={importing && <CircularProgress size={20} />}
        >
          {importing ? t('importModal.importing') : t('importModal.import')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportRacetrackModal; 